import type { SidebarMenuItem } from '../types';

/**
 * Drops the menu entries whose feature the role holds no VIEW on.
 *
 * The FUNCTION features the RBAC matrix scores — Purchase Requests, Request For Quotation, Reverse
 * Auction, Workflow Configuration, RBAC, Organization Setup — are main sidebar menus one-for-one, so an
 * entry names the feature it leads to and this asks the grants about it. Ticking VIEW for a role in the
 * RBAC screen is what puts the menu in that role's sidebar, and unticking it is what takes it away; no
 * code names a role, and no code decides the answer for a particular feature.
 *
 * **Only entries that declare a `feature` are considered.** Everything else is returned exactly as it
 * came: the Dashboard and the other globally available entries, Templates, and the Masters group, whose
 * children are resolved through `MASTER_DEFINITIONS` by `withReadableMasters` and are deliberately not
 * touched here — that behaviour already works and this must not become a second rule about it.
 *
 * VIEW, not READ: every new role holds READ on every item, so READ cannot be what shows a menu. CREATE,
 * UPDATE and DELETE carry VIEW with them, so a role that can act on a screen can always open it.
 *
 * Unknown grants drop nothing — `canReach` answers true before the profile resolves and on a platform
 * that reports no grants at all. Same policy as every other consumer of `useFeatureAccess`, so the menu,
 * the route guard and the screen cannot disagree, and a slow profile never blanks the sidebar.
 */
export function withReadableFeatures(
  items: SidebarMenuItem[],
  canReach: (feature: string) => boolean,
): SidebarMenuItem[] {
  return items.flatMap((item) => {
    if (item.feature !== undefined && !canReach(item.feature)) return [];

    /*
      Recursive so a nested entry that names a feature is governed too. Nothing nested declares one
      today, which is why this changes no group's children on its own.
    */
    if (!item.children) return [item];

    const children = withReadableFeatures(item.children, canReach);
    if (children.length === item.children.length) return [item];

    /*
      A group whose children were ALL dropped goes with them, rather than leaving an accordion that
      opens onto nothing -- the rule `withReadableMasters` already follows for the Masters group, and
      the right one here too: a group's own path is a redirect into its first child, so an empty one
      would lead straight to a refusal.

      This can only ever fire for a group whose children name features. Nothing else reaches it.
    */
    return children.length > 0 ? [{ ...item, children }] : [];
  });
}
